import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'iconsax-react';
import { useNavigate, useParams } from 'react-router-dom';
import { message } from 'antd';
import TitleText from '../../atoms/TitleText';
import InfoText from '../../atoms/InfoText';
import DeleteSellerModal from '../DeleteSellerModal';
import { useDeleteSellerMutation } from '../../../store/services/addSellerService';
import useHandleApiError from '../../../hooks/useHandleApiError';

interface ActionsSidebarProps {
  actions?: {
    canSetupMarketPlace?: boolean;
    addProduct?: boolean;
    editProfile?: boolean;
    deleteProfile?: boolean;
  };
}

const ActionsSidebar: React.FC<ActionsSidebarProps> = ({ actions: sellerActions }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sellerId } = useParams();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteSeller, { isLoading: isDeleting }] = useDeleteSellerMutation();
  const { handleApiError } = useHandleApiError();

  const handleConfirmDelete = async () => {
    if (!sellerId) return;
    try {
      await deleteSeller(sellerId).unwrap();
      message.success(t('sellers.deleteSuccess'));
      setIsDeleteModalOpen(false);
      navigate('/sellers');
    } catch (err: unknown) {
      handleApiError(err);
      setIsDeleteModalOpen(false);
    }
  };

  const allActions = [
    {
      id: 'setupMarketplace',
      title: t('sellers.sellerProfile.actions.setupMarketplace'),
      subtitle: t('sellers.sellerProfile.actions.setupMarketplaceDesc'),
      onClick: () => navigate(`/sellers/${sellerId}/profile/setup-marketplace`),
      visible: sellerActions?.canSetupMarketPlace ?? true,
    },
    {
      id: 'addProduct',
      title: t('sellers.sellerProfile.actions.addProduct'),
      subtitle: t('sellers.sellerProfile.actions.addProductDesc'),
      onClick: () => navigate(`/sellers/${sellerId}/profile/add-product`),
      visible: sellerActions?.addProduct ?? true,
    },
    {
      id: 'editProfile',
      title: t('sellers.sellerProfile.actions.editProfile'),
      subtitle: t('sellers.sellerProfile.actions.editProfileDesc'),
      onClick: () => navigate(`/sellers/${sellerId}/profile-creation?mode=edit`),
      visible: sellerActions?.editProfile ?? true,
    },
    {
      id: 'deleteProfile',
      title: t('sellers.sellerProfile.actions.deleteProfile'),
      subtitle: t('sellers.sellerProfile.actions.deleteProfileDesc'),
      onClick: () => setIsDeleteModalOpen(true),
      visible: sellerActions?.deleteProfile ?? true,
    },
  ];

  const actions = allActions.filter((action) => action.visible);

  return (
    <div className="bg-white dark:bg-dark-surface p-4 rounded-xl shadow-sm border border-gray-100 dark:border-dark-border" data-testid="actions-sidebar-container">
      <div className="h-[25px] flex items-center mb-4" data-testid="actions-sidebar-header">
        <TitleText
          text={t('sellers.sellerProfile.actions.title')}
          className="text-[18px] font-bold text-[#090909] dark:text-dark-text-primary leading-none"
        />
      </div>

      <div className="flex flex-col gap-3" data-testid="actions-list">
        {actions.length === 0 ? (
          <div data-testid="actions-empty" className="py-2">
            <InfoText
              text={t('sellers.noActions')}
              className="!text-[14px] !font-normal !text-light-text-placeholder dark:!text-dark-text-placeholder"
            />
          </div>
        ) : (
          actions.map((action) => (
            <div
              key={action.id}
              onClick={action.onClick}
              data-testid={`action-${action.id}`}
              className="flex items-center justify-between p-4 px-0 cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-hover transition-colors rounded-lg border-b border-[#E5E7EB] dark:border-dark-border last:border-0"
            >
              <div data-testid={`action-info-${action.id}`}>
                <TitleText
                  text={action.title}
                  className="text-[18px] font-semibold text-[#090909] dark:text-dark-text-primary mb-1"
                />
                <InfoText
                  text={action.subtitle}
                  className="text-[14px] text-[#6B7280] dark:text-dark-text-secondary"
                  maxLength="FULL"
                />
              </div>
              <div className="w-[44px] h-[44px] rounded-[12px] bg-[#F2F4F4] dark:bg-gray-800 flex items-center justify-center shrink-0" data-testid={`action-icon-box-${action.id}`}>
                <ArrowRight size="20" className="text-[#090909] dark:text-gray-300 rtl:rotate-180" />
              </div>
            </div>
          ))
        )}
      </div>

      <DeleteSellerModal
        isOpen={isDeleteModalOpen}
        isLoading={isDeleting}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default ActionsSidebar;
